import React, { useEffect, useState, useCallback } from 'react';
import { useDataProvider, useGetIdentity, useNotify, useRefresh } from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { getToken } from '../authentication/authProvider';
import { Funeral } from '../types';
import {
  countDeathCertificates,
  ContractForCount,
  PaymentForCount,
} from '../funerals/deathCertificateCounts';
import { buildPaperworkCompleteMessage, CertificateOrder } from './paperworkCompleteMessage';

const API_URL = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001';
const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;

interface ClientOption {
  id: number;
  name: string;
  email?: string | null;
  type: string;
}

interface PaperworkCompleteNotificationDialogProps {
  open: boolean;
  onClose: () => void;
  funeral: Funeral | null;
  /** Called after a successful send (in addition to the internal refresh). */
  onSent?: () => void;
}

export function PaperworkCompleteNotificationDialog({
  open,
  onClose,
  funeral,
  onSent,
}: PaperworkCompleteNotificationDialogProps) {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [clientOptions, setClientOptions] = useState<ClientOption[]>([]);
  const [certificateOrder, setCertificateOrder] = useState<CertificateOrder | null>(null);
  const [recipientClientId, setRecipientClientId] = useState<number | ''>('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  // "Close without email" — two-step so it can't be hit by accident, and so we
  // can capture why (no email on file, family asked not to be contacted, ...).
  const [confirmingWaive, setConfirmingWaive] = useState(false);
  const [waiveReason, setWaiveReason] = useState('');
  const [waiving, setWaiving] = useState(false);
  const { identity } = useGetIdentity();

  const language: 'en' | 'fr' = funeral?.language === 'fr' ? 'fr' : 'en';
  const defaultSubject = language === 'fr'
    ? 'Cleo - Vos démarches administratives sont complétées'
    : 'Cleo - Your paperwork is complete';

  // Fetch eligible clients (primary, paying, authorized) with email addresses.
  useEffect(() => {
    if (!open || !funeral) return;

    let cancelled = false;
    const collected: ClientOption[] = [];
    const seen = new Set<number>();

    const addClient = async (id: number | undefined | null, type: string) => {
      if (!id || seen.has(Number(id))) return;
      try {
        const response = await dataProvider.getOne('clients', { id });
        const client = response.data as any;
        if (cancelled) return;
        const name = client.name || `${client.first_name || ''} ${client.last_name || ''}`.trim();
        seen.add(Number(id));
        collected.push({
          id: Number(id),
          name,
          email: client.email,
          type,
        });
      } catch {
        /* ignore */
      }
    };

    // Which death certificates the family paid for, so the timing note can
    // say 3–4 weeks for accelerated copies. Same paid-contract rule as the
    // sidebar's DEC row (T-121). Loaded before the clients are set so the
    // message is built once, with everything, and never rebuilt under edits.
    const loadCertificateOrder = async (): Promise<CertificateOrder | null> => {
      try {
        const { data: contracts } = await dataProvider.getManyReference('contracts', {
          target: 'funeral_id',
          id: funeral.id,
          pagination: { page: 1, perPage: 50 },
          sort: { field: 'created_at', order: 'ASC' },
          filter: {},
        });
        const contractIds = (contracts ?? []).map((c) => c.id);
        if (contractIds.length === 0) return null;
        // A plain array filter becomes `eq.1,2` (matches nothing); spell out `in`.
        const { data: payments } = await dataProvider.getList('sb_payments', {
          filter: { 'contract_id@in': `(${contractIds.join(',')})` },
          pagination: { page: 1, perPage: 200 },
          sort: { field: 'created_at', order: 'ASC' },
        });
        const counts = countDeathCertificates(
          contracts as ContractForCount[],
          (payments ?? []) as PaymentForCount[],
        );
        return { standard: counts.standard, expedited: counts.expedited };
      } catch {
        // Fall back to the usual 6–8 weeks wording.
        return null;
      }
    };

    void (async () => {
      const order = await loadCertificateOrder();
      await addClient(funeral.primary_client_id as number | undefined, 'Primary');
      await addClient(funeral.paying_client_id as number | undefined, 'Paying');
      await addClient(funeral.authorized_client_id as number | undefined, 'Authorized');
      if (cancelled) return;
      setCertificateOrder(order);
      setClientOptions(collected);
      const firstWithEmail = collected.find((c) => !!c.email) || collected[0];
      if (firstWithEmail) {
        setRecipientClientId(firstWithEmail.id);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, funeral, dataProvider]);

  // Rebuild the default message when the recipient or funeral changes.
  const buildDefault = useCallback(
    (recipientId: number | '') => {
      if (!funeral) return '';
      const recipient = clientOptions.find((c) => c.id === recipientId);
      const firstName = recipient?.name?.split(' ')[0] || '';
      return buildPaperworkCompleteMessage(
        language,
        firstName,
        funeral.death_certificate_number,
        funeral.benefits_confirmation_number,
        certificateOrder,
      );
    },
    [clientOptions, funeral, language, certificateOrder],
  );

  useEffect(() => {
    if (!open) return;
    setMessage(buildDefault(recipientClientId));
  }, [open, recipientClientId, buildDefault]);

  useEffect(() => {
    if (!open) return;
    setSubject(defaultSubject);
  }, [open, defaultSubject]);

  // Never carry a half-finished waive across openings.
  useEffect(() => {
    if (open) return;
    setConfirmingWaive(false);
    setWaiveReason('');
  }, [open]);

  const recipient = clientOptions.find((c) => c.id === recipientClientId);
  const recipientHasEmail = !!recipient?.email;

  const handleSend = async () => {
    if (!funeral?.id || !recipientClientId) return;
    if (!recipientHasEmail) {
      notify('Selected recipient has no email on file', { type: 'warning' });
      return;
    }
    setSending(true);
    try {
      const token = await getToken();
      const response = await fetch(
        `${baseUrl}/paperwork-complete-notifications/${funeral.id}/send`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            recipient_client_id: recipientClientId,
            subject,
            message,
          }),
        },
      );
      const result = await response.json().catch(() => ({}));
      if (response.ok && result?.status === 'success') {
        notify('Notification sent', { type: 'success' });
        onClose();
        onSent?.();
        refresh();
      } else {
        const msg = result?.message || result?.errors?.[0] || 'Failed to send notification';
        notify(msg, { type: 'error' });
      }
    } catch (err: any) {
      notify(err?.message || 'Failed to send notification', { type: 'error' });
    } finally {
      setSending(false);
    }
  };

  /**
   * Close the case without writing the family. Marking the email waived is what
   * lets the case leave the board's "Delivered" group — see `isClosed` in
   * funerals/caseClosure.ts. Without this, a case that shouldn't be emailed
   * would sit there forever.
   */
  const handleWaive = async () => {
    if (!funeral?.id) return;
    setWaiving(true);
    try {
      await dataProvider.update('funerals', {
        id: funeral.id,
        data: {
          paperwork_email_waived_at: new Date().toISOString(),
          paperwork_email_waived_by: identity?.fullName || null,
          paperwork_email_waived_reason: waiveReason.trim() || null,
        },
        previousData: funeral,
      });
      notify('Case closed without sending the confirmation email', { type: 'info' });
      onClose();
      refresh();
    } catch (err: any) {
      notify(err?.message || 'Failed to close without email', { type: 'error' });
    } finally {
      setWaiving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Paperwork complete — notify customer</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Typography variant="body2" color="text.secondary">
            Both the declaration of death and the benefits application are now marked complete.
            Send the family a short note confirming the paperwork is done.
          </Typography>

          <FormControl fullWidth>
            <InputLabel id="paperwork-recipient-label">Send to</InputLabel>
            <Select
              labelId="paperwork-recipient-label"
              value={recipientClientId}
              label="Send to"
              onChange={(e) => setRecipientClientId(e.target.value as number)}
            >
              {clientOptions.map((c) => (
                <MenuItem key={c.id} value={c.id} disabled={!c.email}>
                  {c.name} ({c.type} Client){c.email ? '' : ' — no email on file'}
                </MenuItem>
              ))}
              {clientOptions.length === 0 && (
                <MenuItem value="" disabled>
                  <em>No clients available</em>
                </MenuItem>
              )}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />

          <TextField
            fullWidth
            multiline
            minRows={10}
            label="Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />

          {!recipientHasEmail && recipientClientId && (
            <Typography variant="body2" color="error">
              Selected recipient has no email address on file.
            </Typography>
          )}

          {confirmingWaive && (
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                This closes the case without emailing the family. Use it when
                writing them isn&apos;t appropriate — no email on file, or the
                family asked not to be contacted.
              </Typography>
              <TextField
                fullWidth
                size="small"
                label="Reason (optional)"
                value={waiveReason}
                onChange={(e) => setWaiveReason(e.target.value)}
              />
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending || waiving}>
          Not now
        </Button>
        {confirmingWaive ? (
          <>
            <Button onClick={() => setConfirmingWaive(false)} disabled={waiving}>
              Back
            </Button>
            <Button onClick={handleWaive} color="warning" disabled={waiving}>
              {waiving ? <CircularProgress size={20} /> : 'Confirm — close without email'}
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={() => setConfirmingWaive(true)}
              color="inherit"
              disabled={sending}
            >
              Close without email
            </Button>
            <Button
              onClick={handleSend}
              variant="contained"
              color="primary"
              disabled={sending || !recipientClientId || !recipientHasEmail}
            >
              {sending ? <CircularProgress size={20} /> : 'Send Email'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
